import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import Slide from '@mui/material/Slide';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const DeleteWithPassword = ({ resourceId, resourceName = "resource", onDelete, navigate }) => {
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // "success" or "error"
  });

  const handleDeleteClick = () => {
    // Retrieve user data from sessionStorage
    const userData = JSON.parse(sessionStorage.getItem("user") || '{}');
    const storedPassword = userData?.password;

    console.log("Stored user data:", userData); // Debug log
    console.log("Stored password:", storedPassword); // Debug log

    if (!storedPassword) {
      setSnackbar({
        open: true,
        message: "No stored password found. Redirecting to login page.",
        severity: "error",
      });
      setTimeout(() => {
        navigate("/", { replace: true });
      }, 2000);
      return;
    }
    setPasswordInput("");
    setPasswordError("");
    setOpenPasswordDialog(true);
  };

  const handlePasswordSubmit = async () => {
    // Retrieve user data from sessionStorage
    const userData = JSON.parse(sessionStorage.getItem("user") || '{}');
    const storedPassword = userData?.password;

    console.log("Entered password:", passwordInput); // Debug log
    console.log("Stored password:", storedPassword); // Debug log

    if (!storedPassword) {
      setPasswordError("No stored password found. Please log in again.");
      setTimeout(() => {
        navigate("/", { replace: true });
      }, 2000);
      return;
    }

    if (passwordInput === storedPassword) {
      try {
        await onDelete(resourceId);
        setSnackbar({
          open: true,
          message: `${resourceName} deleted successfully!`,
          severity: "success",
        });
        setOpenPasswordDialog(false);
        setPasswordInput("");
      } catch (error) {
        setSnackbar({
          open: true,
          message: `Error deleting ${resourceName.toLowerCase()}: ${error.response?.data?.message || error.message}`,
          severity: "error",
        });
      }
    } else {
      setPasswordError("Incorrect password. Please try again.");
    }
  };

  const handlePasswordDialogClose = () => {
    setOpenPasswordDialog(false);
    setPasswordInput("");
    setPasswordError("");
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <Button
        color="error"
        onClick={handleDeleteClick}
        sx={{ textTransform: "none" }}
      >
        Delete
      </Button>

      <Dialog
        open={openPasswordDialog}
        onClose={handlePasswordDialogClose}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Slide}
        TransitionProps={{ direction: "up" }}
        PaperProps={{
          sx: {
            borderRadius: 4,
            border: "2px solid #77c8c7",
            boxShadow: 10,
            bgcolor: "#ffffff",
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#925f96",
            color: "white",
            fontWeight: "bold",
            fontSize: "1.3rem",
            textAlign: "center",
            py: 2,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          }}
        >
          🔐 Admin Password Required
        </DialogTitle>

        <DialogContent sx={{ pt: 3, px: 4 }}>
          <Typography sx={{ mb: 2, color: "#333", fontWeight: 500 }}>
            Please enter the password to delete this{" "}
            <span style={{ color: "#925f96", fontWeight: 600 }}>
              {resourceName.toLowerCase()}
            </span>.
          </Typography>

          <TextField
            label="Password"
            type="password"
            fullWidth
            variant="outlined"
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
            error={!!passwordError}
            helperText={passwordError}
            sx={{
              bgcolor: "#f9f9f9",
              borderRadius: 1,
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#77c8c7",
                },
                "&:hover fieldset": {
                  borderColor: "#925f96",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#925f96",
                },
              },
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 4, pb: 3 }}>
          <Button
            onClick={handlePasswordDialogClose}
            variant="outlined"
            sx={{
              borderColor: "#925f96",
              color: "#925f96",
              "&:hover": {
                bgcolor: "#f3e9f3",
                borderColor: "#925f96",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePasswordSubmit}
            disabled={!passwordInput}
            variant="contained"
            sx={{
              bgcolor: "#77c8c7",
              color: "white",
              fontWeight: 600,
              "&:hover": {
                bgcolor: "#5ab3b2",
              },
            }}
          >
            Confirm Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={Slide}
      >
        <Alert
          icon={<CheckCircleIcon fontSize="inherit" />}
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            bgcolor:
              snackbar.severity === "success"
                ? "linear-gradient(90deg, #77c8c7 0%, #925f96 100%)"
                : "#f44336",
            color: "white",
            fontWeight: 600,
            boxShadow: 4,
            borderRadius: 2,
            "& .MuiAlert-icon": {
              color: "white",
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DeleteWithPassword;